import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, call } from '../client';
import { qk } from '../keys';
import type { ApplicationCreate } from '../schema';

function useInvalidate(...keys: ReadonlyArray<readonly unknown[]>) {
  const qc = useQueryClient();
  return () => {
    for (const k of keys) void qc.invalidateQueries({ queryKey: k });
  };
}

// ---- watchlists -----------------------------------------------------------------------------

export function useWatchlists() {
  return useQuery({ queryKey: qk.watchlists.list, queryFn: () => call(api.GET('/api/v1/watchlists')) });
}

export function useWatchlist(id: string | undefined) {
  return useQuery({
    queryKey: qk.watchlists.detail(id ?? ''),
    queryFn: () => call(api.GET('/api/v1/watchlists/{watchlist_id}', { params: { path: { watchlist_id: id! } } })),
    enabled: Boolean(id),
  });
}

export function useCreateWatchlist() {
  const done = useInvalidate(qk.watchlists.all);
  return useMutation({ mutationFn: (name: string) => call(api.POST('/api/v1/watchlists', { body: { name } })), onSuccess: done });
}

export function useRenameWatchlist(id: string) {
  const done = useInvalidate(qk.watchlists.all);
  return useMutation({
    mutationFn: (name: string) => call(api.PATCH('/api/v1/watchlists/{watchlist_id}', { params: { path: { watchlist_id: id } }, body: { name } })),
    onSuccess: done,
  });
}

export function useDeleteWatchlist() {
  const done = useInvalidate(qk.watchlists.all, qk.ranking, qk.applications);
  return useMutation({
    mutationFn: (id: string) => call(api.DELETE('/api/v1/watchlists/{watchlist_id}', { params: { path: { watchlist_id: id } } })),
    onSuccess: done,
  });
}

/** Several at once (`POST /watchlists/delete`): one request for up to 200; ids already gone are skipped. */
export function useDeleteWatchlists() {
  const done = useInvalidate(qk.watchlists.all, qk.ranking, qk.applications);
  return useMutation({
    mutationFn: (ids: string[]) => call(api.POST('/api/v1/watchlists/delete', { body: { watchlist_ids: ids } })),
    onSuccess: done,
  });
}

export function useAddWatchlistItems(id: string) {
  const done = useInvalidate(qk.watchlists.all, qk.ranking, qk.applications);
  return useMutation({
    mutationFn: (instrumentIds: string[]) =>
      call(api.POST('/api/v1/watchlists/{watchlist_id}/items/batch', { params: { path: { watchlist_id: id } }, body: { instrument_ids: instrumentIds } })),
    onSuccess: done,
  });
}

export function useRemoveWatchlistItems(id: string) {
  const done = useInvalidate(qk.watchlists.all, qk.ranking, qk.applications);
  return useMutation({
    mutationFn: (instrumentIds: string[]) =>
      call(api.POST('/api/v1/watchlists/{watchlist_id}/items/delete', { params: { path: { watchlist_id: id } }, body: { instrument_ids: instrumentIds } })),
    onSuccess: done,
  });
}

export function useUnbindStrategy(watchlistId: string) {
  const done = useInvalidate(qk.watchlists.all, qk.ranking, qk.applications, qk.strategies.all);
  return useMutation({
    mutationFn: (strategyId: string) =>
      call(
        api.DELETE('/api/v1/watchlists/{watchlist_id}/strategies/{strategy_id}', {
          params: { path: { watchlist_id: watchlistId, strategy_id: strategyId } },
        }),
      ),
    onSuccess: done,
  });
}

/** The same unbind from the strategy's side, where the watchlist varies per call. */
export function useUnbindWatchlist(strategyId: string) {
  const done = useInvalidate(qk.watchlists.all, qk.ranking, qk.applications, qk.strategies.all);
  return useMutation({
    mutationFn: (watchlistId: string) =>
      call(
        api.DELETE('/api/v1/watchlists/{watchlist_id}/strategies/{strategy_id}', {
          params: { path: { watchlist_id: watchlistId, strategy_id: strategyId } },
        }),
      ),
    onSuccess: done,
  });
}

// ---- applications ---------------------------------------------------------------------------

export function useStrategyApplications(strategyId: string | undefined) {
  return useQuery({
    queryKey: [...qk.applications, strategyId ?? ''],
    queryFn: () => call(api.GET('/api/v1/strategy-applications', { params: { query: { strategy_id: strategyId } } })),
    enabled: Boolean(strategyId),
  });
}

export function useApply() {
  const done = useInvalidate(qk.applications, qk.ranking, qk.strategies.all, qk.watchlists.all);
  return useMutation({ mutationFn: (body: ApplicationCreate) => call(api.POST('/api/v1/strategy-applications', { body })), onSuccess: done });
}

export function useRemoveApplications() {
  const done = useInvalidate(qk.applications, qk.ranking, qk.strategies.all);
  return useMutation({
    mutationFn: (ids: string[]) => call(api.POST('/api/v1/strategy-applications/delete', { body: { application_ids: ids } })),
    onSuccess: done,
  });
}

// ---- templates & sharing --------------------------------------------------------------------

export function useTemplates() {
  return useQuery({ queryKey: qk.templates, queryFn: () => call(api.GET('/api/v1/strategy-templates')), staleTime: 10 * 60_000 });
}

export function useCopyTemplate() {
  const done = useInvalidate(qk.strategies.all);
  return useMutation({
    mutationFn: (templateId: string) =>
      call(api.POST('/api/v1/strategy-templates/{template_id}/copy', { params: { path: { template_id: templateId } } })),
    onSuccess: done,
  });
}

export function useShareStrategy() {
  return useMutation({
    mutationFn: (strategyId: string) => call(api.POST('/api/v1/strategies/{strategy_id}/share', { params: { path: { strategy_id: strategyId } } })),
  });
}

export function useSharedStrategy(code: string, { enabled = true }: { enabled?: boolean } = {}) {
  return useQuery({
    queryKey: ['strategy-shares', code],
    queryFn: () => call(api.GET('/api/v1/strategy-shares/{code}', { params: { path: { code } } })),
    enabled,
  });
}

/** A share as someone without an account sees it: no settings, no account needed. */
export function usePublicShare(code: string) {
  return useQuery({
    queryKey: ['strategy-shares', 'public', code],
    queryFn: () => call(api.GET('/api/v1/public/strategy-shares/{code}', { params: { path: { code } } })),
    // A missing, withdrawn or rate-limited link answers the same on a retry.
    retry: false,
    staleTime: 5 * 60_000,
  });
}

/** Save a share as the caller's own strategy — suspended when their plan cannot run it. */
export const COPY_SHARE_KEY = ['strategy-shares', 'copy'] as const;

export function useCopyShare() {
  const after = useInvalidate(qk.strategies.all, qk.me);
  return useMutation({
    // Keyed, so a screen can tell a save is already under way elsewhere (the phone gate).
    mutationKey: COPY_SHARE_KEY,
    mutationFn: (code: string) =>
      call(api.POST('/api/v1/strategy-shares/{code}/copy', { params: { path: { code } } })),
    onSuccess: after,
  });
}
